import { useState } from 'react';
import { getInternalNotes, saveInternalNotes } from '../../utils/storage';
import { getSettings } from '../../utils/storage';

const MAX_CHARS = 1000;

export default function InternalNotes({ vehicle, garage, onUpdateVehicle, onToast, canLog }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  // Only show for team garages (garages with collaborators)
  if (!garage?.collaborators?.length) return null;

  const notes = vehicle.internalNotes || null;
  const hasNotes = notes && notes.content;

  function handleStartEdit() {
    setDraft(notes?.content || '');
    setEditing(true);
  }

  function handleCancel() {
    setEditing(false);
    setDraft('');
  }

  function handleSave() {
    const trimmed = draft.trim();
    if (!trimmed) return;

    const settings = getSettings();
    const userName = settings.profile?.name || 'Unknown';
    const userEmail = settings.profile?.email || 'unknown@propermiles.com';

    const updated = saveInternalNotes(vehicle.id, trimmed, userName, userEmail);
    if (updated) {
      // Update vehicle in parent state
      onUpdateVehicle({ ...vehicle, internalNotes: updated });
      onToast?.('Notes saved');
    }
    setEditing(false);
    setDraft('');
  }

  function formatDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' at ' +
      d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }

  return (
    <div style={{ marginTop: 20 }}>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <h2
          style={{
            fontSize: '0.8125rem',
            fontWeight: 600,
            color: 'var(--text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          Internal Notes
        </h2>
        <span
          style={{
            padding: '2px 8px',
            borderRadius: 100,
            background: 'rgba(59, 130, 246, 0.12)',
            color: '#3b82f6',
            fontSize: '0.625rem',
            fontWeight: 600,
          }}
        >
          Team Only
        </span>
      </div>

      {/* Edit mode */}
      {editing && (
        <div
          className="card"
          style={{ padding: 16 }}
        >
          <textarea
            value={draft}
            onChange={(e) => {
              if (e.target.value.length <= MAX_CHARS) setDraft(e.target.value);
            }}
            placeholder="Add notes for your team (e.g., 'Owner prefers valet parking only', 'Do not move without approval', 'Battery tender required - Bay 12')"
            style={{
              width: '100%',
              minHeight: 120,
              padding: 12,
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'var(--bg-elevated)',
              color: 'var(--text-primary)',
              fontSize: '0.875rem',
              lineHeight: 1.5,
              resize: 'vertical',
              outline: 'none',
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
            <span style={{ fontSize: '0.6875rem', color: draft.length > MAX_CHARS * 0.9 ? 'var(--warning)' : 'var(--text-muted)' }}>
              {draft.length} / {MAX_CHARS}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn btn-secondary"
                onClick={handleCancel}
                style={{ padding: '8px 16px', fontSize: '0.8125rem', width: 'auto' }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={!draft.trim()}
                style={{ padding: '8px 16px', fontSize: '0.8125rem', width: 'auto' }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!editing && !hasNotes && (
        <div
          className="card"
          style={{
            textAlign: 'center',
            padding: '28px 20px',
          }}
        >
          <div style={{ fontSize: '1.5rem', marginBottom: 8 }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
          </div>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 4 }}>
            Add internal notes for your team
          </p>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.4 }}>
            Storage location, owner preferences, special instructions
          </p>
          {canLog !== false && (
            <button
              className="btn btn-secondary"
              onClick={handleStartEdit}
              style={{ width: 'auto', padding: '8px 20px', fontSize: '0.8125rem', margin: '0 auto' }}
            >
              Add Note
            </button>
          )}
        </div>
      )}

      {/* View mode */}
      {!editing && hasNotes && (
        <div
          className="card"
          style={{ padding: 16, position: 'relative' }}
        >
          {/* Edit button — only with LOG_ACCESS+ */}
          {canLog !== false && (
            <button
              onClick={handleStartEdit}
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'var(--accent)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px 8px',
              }}
            >
              Edit
            </button>
          )}

          {/* Note content */}
          <p
            style={{
              fontSize: '0.875rem',
              color: 'var(--text-primary)',
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
              paddingRight: 40,
              marginBottom: 12,
            }}
          >
            {notes.content}
          </p>

          {/* Footer */}
          <div
            style={{
              borderTop: '1px solid var(--border)',
              paddingTop: 10,
              fontSize: '0.6875rem',
              color: 'var(--text-muted)',
            }}
          >
            Last updated by {notes.lastUpdatedByName} on {formatDate(notes.lastUpdatedAt)}
          </div>

          {/* Edit history toggle */}
          {notes.history && notes.history.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <button
                onClick={() => setShowHistory(!showHistory)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: 'var(--text-secondary)',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{
                    transform: showHistory ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.15s',
                  }}
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
                View History ({notes.history.length})
              </button>

              {showHistory && (
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {notes.history.map((entry, i) => (
                    <div
                      key={i}
                      style={{
                        padding: '10px 12px',
                        background: 'var(--bg-elevated)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '0.75rem',
                      }}
                    >
                      <div style={{ color: 'var(--text-muted)', marginBottom: 4 }}>
                        {formatDate(entry.updatedAt)} by {entry.updatedByName}
                      </div>
                      <div style={{ color: 'var(--text-secondary)', lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>
                        {entry.content}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
